import { useAuthentication } from "@src/auth"
import { config } from "@src/constants"
import { usePartner } from "@src/partner"
import { useUser } from "@src/user"
import React from "react"
import { Button, Form, InputGroup } from "react-bootstrap"
import { useTranslation } from "react-i18next"

const styles = {
  serialNumberInput: {
    marginTop: "15px",
  },
}

export const AddTrackers: React.FC = () => {
  const { t } = useTranslation()
  const { userEmail } = useAuthentication()
  const { partner } = usePartner()
  const { user } = useUser()
  const [serialNumbers, setSerialNumbers] = React.useState("")
  const [newSerial, setNewSerial] = React.useState("")
  const [invalidSerials, setInvalidSerials] = React.useState<string[]>([])

  const selectedSerials = React.useMemo(
    () =>
      Array.from(
        new Set(
          serialNumbers
            .split(";")
            .map((s) => s.trim())
            .filter(Boolean),
        ),
      ),
    [serialNumbers],
  )

  const handleSelectorChange = (serials: string[]) => {
    setSerialNumbers(serials.join("; "))
  }

  const createEmailLink = () => {
    const subject = "Request to Add New Trackers to My Account"

    const body = `Dear Customer Care Team,

I would like to request adding new trackers to my account.

My account details:
- Email: ${userEmail}
- Name: ${user?.firstname} ${user?.lastname}
- Address: ${user?.address}
- Partner: ${partner?.name} (${partner?.uuid})

Trackers to be added:
- Serial Numbers: ${serialNumbers || "[Please fill in serial numbers]"}

Could you please add these trackers to my account?

Thank you for your assistance.

Best regards,
${user?.firstname} ${user?.lastname}`

    return `mailto:${config.customerCareEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
  }

  return (
    <div>
      <div>
        <h5>{t("settings.partner.add_trackers_title")}</h5>
        <p className="mb-3">{t("settings.partner.add_trackers_description")}</p>

        <div className="mb-2" style={styles.serialNumberInput}>
          <label className="form-label">{t("settings.partner.add_serial_numbers_label")}:</label>
          <div className="mb-2 d-flex flex-wrap gap-2">
            {selectedSerials.map((sn) => (
              <span key={sn} className="badge bg-secondary">
                {sn}
                <span
                  role="button"
                  aria-label="remove"
                  className="ms-2"
                  onClick={() => handleSelectorChange(selectedSerials.filter((s) => s !== sn))}
                >
                  ×
                </span>
              </span>
            ))}
          </div>
          <InputGroup className="mt-2 align-items-start">
            <Form.Control
              type="text"
              value={newSerial}
              placeholder={t("settings.partner.serial_numbers_placeholder")}
              onChange={(e) => setNewSerial(e.target.value)}
              isInvalid={invalidSerials.length > 0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === ";" || e.key === " ") {
                  e.preventDefault()
                  const tokens = newSerial
                    .split(/[\s,]+/)
                    .map((s) => s.trim())
                    .filter(Boolean)
                  if (tokens.length) {
                    const valid = tokens.filter((s) => /^\d{9,10}$/.test(s))
                    const invalid = tokens.filter((s) => !/^\d{9,10}$/.test(s))
                    setInvalidSerials(invalid)
                    if (valid.length) {
                      const merged = Array.from(new Set([...selectedSerials, ...valid]))
                      setSerialNumbers(merged.join("; "))
                      setNewSerial("")
                    }
                  }
                }
              }}
              onBlur={() => {
                const tokens = newSerial
                  .split(/[\s,]+/)
                  .map((s) => s.trim())
                  .filter(Boolean)
                if (tokens.length) {
                  const valid = tokens.filter((s) => /^\d{9,10}$/.test(s))
                  const invalid = tokens.filter((s) => !/^\d{9,10}$/.test(s))
                  setInvalidSerials(invalid)
                  if (valid.length) {
                    const merged = Array.from(new Set([...selectedSerials, ...valid]))
                    setSerialNumbers(merged.join("; "))
                    setNewSerial("")
                  }
                }
              }}
              onPaste={(e) => {
                const paste = e.clipboardData.getData("text")
                if (paste) {
                  e.preventDefault()
                  const tokens = paste
                    .split(/[\s,]+/)
                    .map((s) => s.trim())
                    .filter(Boolean)
                  if (tokens.length) {
                    const valid = tokens.filter((s) => /^\d{9,10}$/.test(s))
                    const invalid = tokens.filter((s) => !/^\d{9,10}$/.test(s))
                    setInvalidSerials(invalid)
                    if (valid.length) {
                      const merged = Array.from(new Set([...selectedSerials, ...valid]))
                      setSerialNumbers(merged.join("; "))
                      setNewSerial("")
                    }
                  }
                }
              }}
            />
            <Button
              variant="outline-secondary"
              onClick={() => {
                const tokens = newSerial
                  .split(/[\s,]+/)
                  .map((s) => s.trim())
                  .filter(Boolean)
                if (tokens.length) {
                  const valid = tokens.filter((s) => /^\d{9,10}$/.test(s))
                  const invalid = tokens.filter((s) => !/^\d{9,10}$/.test(s))
                  setInvalidSerials(invalid)
                  if (valid.length) {
                    const merged = Array.from(new Set([...selectedSerials, ...valid]))
                    setSerialNumbers(merged.join("; "))
                    setNewSerial("")
                  }
                }
              }}
            >
              {t("settings.partner.add")}
            </Button>
          </InputGroup>
          {invalidSerials.length > 0 && (
            <div className="text-danger small mt-1">
              {t("settings.partner.invalid_serial_number")} ({invalidSerials.join("; ")})
            </div>
          )}
        </div>

        <Button variant="primary" href={createEmailLink()} disabled={selectedSerials.length === 0}>
          {t("settings.partner.send_email_button")}
        </Button>
      </div>
    </div>
  )
}
